import { useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'

import {
  cadVersionUploadedAsync,
  uploadFile,
  getCadVersion,
  View,
} from '@/lib/api/client'
import { useGetProject } from '@/services/queries/projects'
import {
  useGetCadVersion,
  useLatestCadVersion,
  useListCadVersions,
} from '@/services/queries/cad_versions'
import {
  QUERY_KEYS as DOCUMENTS_QUERY_KEYS,
  useDocument,
} from '@/services/queries/documents'
import { getViewQuery, useListPageViews } from '@/services/queries/views'
import { RawAssemblyTree } from '@/state'
import { compressFile } from '@/lib/gzip'

import { useDocumentPageParams } from './hooks'
import CustomError from '@/lib/api/CustomError'
import { useCad } from '@/services/queries/cads/queries/useCad'
import { useListDocumentVersions } from '@/services/queries/document_versions'
import { useListDocumentPagesQuery } from '@/services/queries/document_pages'
import { useListImages } from '@/services/queries/images'

export const useDocumentPageQuery = () => {
  const { projectId, documentId, cv, dv } = useDocumentPageParams()

  const { data: project, isLoading: isLoadingProject } = useGetProject({
    projectId,
  })

  const { data: latestDocument, isLoading: isLoadingLatestDocument } =
    useDocument({
      documentId,
    })

  const { data: latestCad, isLoading: isLoadingLatestCad } = useCad({
    cadId: latestDocument?.cad,
  })

  const { data: documentVersions, isLoading: isLoadingDocumentVersions } =
    useListDocumentVersions({
      documentId,
    })

  const { data: cadVersions, isLoading: isLoadingCadVersions } =
    useListCadVersions({
      cadId: latestCad?.id,
    })

  const orderedDocumentVersions = useMemo(
    () =>
      documentVersions?.sort(
        (a, b) => (b?.version_number || 0) - (a?.version_number || 0),
      ) || [],
    [documentVersions],
  )

  const latestDocumentVersion = useMemo(
    () =>
      orderedDocumentVersions.length > 0
        ? orderedDocumentVersions[0]
        : undefined,
    [orderedDocumentVersions],
  )

  const documentVersion = useMemo(
    () =>
      dv
        ? documentVersions?.find((v) => v.id === dv) || latestDocumentVersion
        : latestDocumentVersion,
    [documentVersions, dv, latestDocumentVersion],
  )

  const { data: cadVersion, isLoading: isLoadingCadVersion } = useGetCadVersion(
    {
      cadVersionId: documentVersion?.cad_version,
    },
  )

  const { data: latestCadVersion, isLoading: isLoadingLatestCadVersion } =
    useLatestCadVersion({
      cadId: latestCad?.id,
    })

  const selectedLatestCadVersion = useMemo(
    () => cadVersion || latestCadVersion,
    [cadVersion, latestCadVersion],
  )

  const { data: documentPages, isLoading: isLoadingDocumentPages } =
    useListDocumentPagesQuery({ documentVersionId: documentVersion?.id })

  const { data: views, isLoading: isLoadingViews } = useListPageViews({
    cadVersionId: selectedLatestCadVersion?.id,
    documentVersionId: documentVersion?.id,
  })

  const { data: images, isLoading: isLoadingImages } = useListImages({
    documentVersionId: documentVersion?.id,
    enabled: true,
  })

  const isLoading = useMemo(
    () =>
      isLoadingProject ||
      isLoadingLatestDocument ||
      isLoadingLatestCad ||
      isLoadingDocumentVersions ||
      isLoadingCadVersions ||
      isLoadingCadVersion ||
      isLoadingLatestCadVersion ||
      isLoadingDocumentPages ||
      isLoadingViews ||
      isLoadingImages,
    [
      isLoadingProject,
      isLoadingLatestDocument,
      isLoadingLatestCad,
      isLoadingDocumentVersions,
      isLoadingCadVersions,
      isLoadingCadVersion,
      isLoadingLatestCadVersion,
      isLoadingDocumentPages,
      isLoadingViews,
      isLoadingImages,
    ],
  )

  const isLatestCadVersion = useMemo(
    () => !cv || cv === selectedLatestCadVersion?.id,
    [cv, selectedLatestCadVersion],
  )

  const isLatestDocumentVersion = useMemo(
    () => latestDocumentVersion && (!dv || dv === latestDocumentVersion.id),
    [dv, latestDocumentVersion],
  )

  const rawAssemblyTree = useMemo(
    () =>
      selectedLatestCadVersion?.assembly_tree as RawAssemblyTree | undefined,
    [selectedLatestCadVersion],
  )

  return {
    data: {
      documentId: documentVersion?.document,
      documentPages,
      documentVersions,
      documentVersion,
      documentType: latestDocument?.document_type,
      doc: latestDocument,
      views,
      images,
      cadVersions,
      project,
      rawAssemblyTree,
      version: selectedLatestCadVersion,
      latestDocumentVersion,
      isLatestCadVersion,
      isLatestDocumentVersion,
    },
    isLoading,
  }
}

export const useDocumentViewsQuery = (viewIds: string[]) => {
  return useQuery({
    queryKey: [
      DOCUMENTS_QUERY_KEYS.DOCUMENT_VIEW,
      { viewIds: viewIds.join(',') },
    ],
    queryFn: async () => {
      const views: View[] = []
      for (const viewId of viewIds) {
        const view = await getViewQuery({ viewId })
        views.push(view)
      }
      return { views }
    },
    enabled: Array.isArray(viewIds),
  })
}

export const useReuploadCadMutation = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      cadVersionId,
      file,
    }: {
      projectId: string
      documentId: string
      cadVersionId: string
      file: File
    }) => {
      const cadVersion = await getCadVersion(cadVersionId)
      const uploadUrl = cadVersion.upload_url
      if (!uploadUrl) {
        throw new Error('Failed to get upload URL')
      }

      const uploadedUrlObj = new URL(uploadUrl)
      const usesFileCompression = uploadedUrlObj.pathname.endsWith('.gz')

      const updatedFile = usesFileCompression ? await compressFile(file) : file

      await uploadFile(uploadUrl, updatedFile)
      await cadVersionUploadedAsync(cadVersionId as string)

      return { projectId, cadVersionId, documentId }
    },
    onSuccess: ({ projectId, cadVersionId, documentId }) => {
      window.location.href = `/p/${projectId}/document/${documentId}/cad?cv=${cadVersionId}`
    },
    onError: (e: any) => {
      if (e instanceof CustomError) {
        toast({
          title: 'Error',
          description: e.message,
          variant: 'destructive',
        })
      } else {
        toast({
          variant: 'destructive',
          title: 'Uh oh! Something went wrong.',
          description: 'There was a problem with your request.',
        })
      }
    },
  })
}
